import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Warehouse as WarehouseIcon, PackagePlus, ArrowDownToLine, History,
  Boxes, Download, Search, Plus, RefreshCw, AlertTriangle, PackageX,
} from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIST = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).replace(',', '');
  } catch { return '-'; }
};

// "12,540" with Indian grouping
const formatPieces = (n) => {
  const v = Number(n || 0);
  try { return v.toLocaleString('en-IN'); } catch { return String(v); }
};

// Short Indian currency: 1,200 → "1,200" • 1,25,000 → "1.25L" • 2,15,00,000 → "2.15Cr"
const formatIndianCurrencyShort = (n) => {
  const v = Number(n || 0);
  if (!v) return '0';
  if (v >= 1e7) return `${(v / 1e7).toFixed(v >= 1e8 ? 1 : 2)}Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(v >= 1e6 ? 1 : 2)}L`;
  try { return v.toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
  catch { return String(Math.round(v)); }
};

const TABS = [
  { id: 'inventory', label: 'Inventory', icon: Boxes },
  { id: 'inbound', label: 'Inbound', icon: PackagePlus },
  { id: 'adjustments', label: 'Adjustments', icon: ArrowDownToLine },
  { id: 'racks', label: 'Racks', icon: WarehouseIcon },
  { id: 'movements', label: 'History', icon: History },
  { id: 'out-of-stock', label: 'Out of Stock', icon: PackageX },
];

export const Warehouse = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');

  return (
    <Layout>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <WarehouseIcon className="w-7 h-7 text-indigo-600" />
          Warehouse Management
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Rack-based inventory with FIFO deduction and full audit trail
        </p>
      </div>

      <div className="border-b border-slate-200 mb-4 sm:mb-6 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`warehouse-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  isActive
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'inventory' && <InventoryTab />}
      {activeTab === 'inbound' && <InboundTab user={user} />}
      {activeTab === 'adjustments' && <AdjustmentsTab user={user} />}
      {activeTab === 'racks' && <RacksTab user={user} />}
      {activeTab === 'movements' && <MovementsTab />}
      {activeTab === 'out-of-stock' && <OutOfStockTab />}
    </Layout>
  );
};

// ============ INVENTORY TAB ============
const InventoryTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  // Filters
  const [filterRack, setFilterRack] = useState('');
  const [filterProductName, setFilterProductName] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterMinQty, setFilterMinQty] = useState('');
  const [filterMaxQty, setFilterMaxQty] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');

  // Debounce all text/number filters together
  const debouncedSearch = useDebounce(search, 300);
  const debouncedRack = useDebounce(filterRack, 300);
  const debouncedProductName = useDebounce(filterProductName, 300);
  const debouncedSize = useDebounce(filterSize, 300);
  const debouncedMinQty = useDebounce(filterMinQty, 300);
  const debouncedMaxQty = useDebounce(filterMaxQty, 300);
  const debouncedFromDate = useDebounce(filterFromDate, 300);
  const debouncedToDate = useDebounce(filterToDate, 300);
  const debouncedMinPrice = useDebounce(filterMinPrice, 300);
  const debouncedMaxPrice = useDebounce(filterMaxPrice, 300);

  const buildParams = useCallback((includePage = true) => {
    const p = new URLSearchParams();
    if (includePage) { p.set('page', String(page)); p.set('per_page', '50'); }
    if (debouncedSearch.trim()) p.set('search', debouncedSearch.trim());
    if (debouncedRack.trim()) p.set('rack_number', debouncedRack.trim().toUpperCase());
    if (debouncedProductName.trim()) p.set('product_name', debouncedProductName.trim());
    if (debouncedSize.trim()) p.set('size', debouncedSize.trim());
    if (debouncedMinQty !== '') p.set('min_qty', String(debouncedMinQty));
    if (debouncedMaxQty !== '') p.set('max_qty', String(debouncedMaxQty));
    if (debouncedFromDate) p.set('last_updated_from', debouncedFromDate);
    if (debouncedToDate) p.set('last_updated_to', debouncedToDate);
    if (debouncedMinPrice !== '') p.set('min_price', String(debouncedMinPrice));
    if (debouncedMaxPrice !== '') p.set('max_price', String(debouncedMaxPrice));
    return p;
  }, [page, debouncedSearch, debouncedRack, debouncedProductName, debouncedSize,
      debouncedMinQty, debouncedMaxQty, debouncedFromDate, debouncedToDate,
      debouncedMinPrice, debouncedMaxPrice]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(true);
      const r = await axios.get(`${API}/warehouse/inventory?${params}`);
      setRows(r.data.items || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Lightweight totals across ALL matching rows (not just current page)
  const [summary, setSummary] = useState({ sku_count: 0, total_pieces: 0, total_stock_value: 0 });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryParams = useMemo(() => buildParams(false).toString(), [buildParams]);

  useEffect(() => {
    let active = true;
    setSummaryLoading(true);
    axios.get(`${API}/warehouse/inventory/summary?${summaryParams}`)
      .then(r => { if (active) setSummary(r.data || { sku_count: 0, total_pieces: 0, total_stock_value: 0 }); })
      .catch(() => { if (active) setSummary({ sku_count: 0, total_pieces: 0, total_stock_value: 0 }); })
      .finally(() => { if (active) setSummaryLoading(false); });
    return () => { active = false; };
  }, [summaryParams]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedRack, debouncedProductName, debouncedSize,
      debouncedMinQty, debouncedMaxQty, debouncedFromDate, debouncedToDate,
      debouncedMinPrice, debouncedMaxPrice]);

  const handleExport = async () => {
    try {
      const params = buildParams(false);
      const r = await axios.get(`${API}/warehouse/inventory/export?${params}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse_stock_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const clearFilters = () => {
    setSearch(''); setFilterRack(''); setFilterProductName(''); setFilterSize('');
    setFilterMinQty(''); setFilterMaxQty(''); setFilterFromDate(''); setFilterToDate('');
    setFilterMinPrice(''); setFilterMaxPrice('');
  };

  const hasActiveFilters = !!(debouncedSearch || debouncedRack || debouncedProductName ||
    debouncedSize || debouncedMinQty !== '' || debouncedMaxQty !== '' ||
    debouncedFromDate || debouncedToDate || debouncedMinPrice !== '' || debouncedMaxPrice !== '');

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by design number..."
            data-testid="warehouse-inventory-search"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(s => !s)}
            data-testid="warehouse-inventory-toggle-filters"
            className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            Filters {hasActiveFilters && <span className="bg-indigo-600 text-white rounded-full px-1.5 text-xs">•</span>}
          </button>
          <button
            onClick={fetchInventory}
            data-testid="warehouse-inventory-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            data-testid="warehouse-export-excel"
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="warehouse-inventory-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <FilterInput label="Rack Number" value={filterRack} onChange={setFilterRack} placeholder="e.g. R-205" testid="filter-rack" />
            <FilterInput label="Design Number" value={search} onChange={setSearch} placeholder="Design #" testid="filter-design" />
            <FilterInput label="Product (Type / Name)" value={filterProductName} onChange={setFilterProductName} placeholder="Product name" testid="filter-product-name" />
            <FilterInput label="Size" value={filterSize} onChange={setFilterSize} placeholder="e.g. XS" testid="filter-size" />
            <div className="grid grid-cols-2 gap-2">
              <FilterInput label="Min Qty" value={filterMinQty} onChange={setFilterMinQty} type="number" testid="filter-min-qty" />
              <FilterInput label="Max Qty" value={filterMaxQty} onChange={setFilterMaxQty} type="number" testid="filter-max-qty" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FilterInput label="Min Price (₹)" value={filterMinPrice} onChange={setFilterMinPrice} type="number" testid="filter-min-price" />
              <FilterInput label="Max Price (₹)" value={filterMaxPrice} onChange={setFilterMaxPrice} type="number" testid="filter-max-price" />
            </div>
            <FilterInput label="Last Updated From" value={filterFromDate} onChange={setFilterFromDate} type="date" testid="filter-from-date" />
            <FilterInput label="Last Updated To" value={filterToDate} onChange={setFilterToDate} type="date" testid="filter-to-date" />
          </div>
          <div className="flex justify-end">
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="filter-clear">Clear all filters</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm text-slate-500">
            {loading ? 'Loading...' : `${total} stock rows`}
          </div>
          <div
            data-testid="warehouse-inventory-summary"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-800 rounded-full text-sm font-medium"
          >
            {summaryLoading ? (
              <span className="text-slate-500">Calculating totals…</span>
            ) : (
              <>
                <span className="tabular-nums" data-testid="summary-total-pieces">
                  {formatPieces(summary.total_pieces)} total pcs
                </span>
                <span className="text-indigo-400">•</span>
                <span className="tabular-nums" data-testid="summary-total-value">
                  ₹{formatIndianCurrencyShort(summary.total_stock_value)} stock value at bulk price
                </span>
                {hasActiveFilters && (
                  <span className="ml-2 text-xs text-indigo-600 bg-white border border-indigo-200 px-2 py-0.5 rounded">
                    filtered
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Rack</th>
                <th className="px-4 py-2 text-left">Design</th>
                <th className="px-4 py-2 text-left">Product</th>
                <th className="px-4 py-2 text-left">Size</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Bulk Price ₹</th>
                <th className="px-4 py-2 text-left">Last Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">No stock available</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.rack_number}-${r.design_number}-${r.size}-${i}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">{r.rack_number}</td>
                  <td className="px-4 py-2 font-mono text-slate-700">{r.design_number}</td>
                  <td className="px-4 py-2 text-slate-700">{r.product_name || '-'}</td>
                  <td className="px-4 py-2">{r.size}</td>
                  <td className="px-4 py-2 text-right font-semibold">{r.quantity}</td>
                  <td className="px-4 py-2 text-right text-slate-700">
                    {(r.bulk_price ?? null) === null ? '-' : `₹${Number(r.bulk_price).toFixed(2)}`}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{formatIST(r.last_updated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
};

const FilterInput = ({ label, value, onChange, placeholder = '', type = 'text', testid }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
  </div>
);

// ============ INBOUND TAB ============
const InboundTab = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [racks, setRacks] = useState([]);
  const [rackLoading, setRackLoading] = useState(false);
  const [designNumber, setDesignNumber] = useState('');
  const [size, setSize] = useState('');
  const [rackNumber, setRackNumber] = useState('');
  const [quantity, setQuantity] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);
  const [rackSearch, setRackSearch] = useState('');
  const debouncedRackSearch = useDebounce(rackSearch, 300);
  const [submitting, setSubmitting] = useState(false);

  // Debounced server-side product search
  useEffect(() => {
    let active = true;
    const run = async () => {
      setProductLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '30' });
        if (debouncedProductSearch.trim()) params.set('search', debouncedProductSearch.trim());
        const r = await axios.get(`${API}/products?${params}`);
        if (!active) return;
        setProducts(Array.isArray(r.data?.products) ? r.data.products : []);
      } catch {
        if (active) setProducts([]);
      } finally {
        if (active) setProductLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [debouncedProductSearch]);

  // Debounced server-side rack search (scales beyond per_page cap)
  useEffect(() => {
    let active = true;
    const run = async () => {
      setRackLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '50', active_only: 'true' });
        if (debouncedRackSearch.trim()) params.set('search', debouncedRackSearch.trim());
        const r = await axios.get(`${API}/warehouse/racks?${params}`);
        if (!active) return;
        setRacks(Array.isArray(r.data?.racks) ? r.data.racks : []);
      } catch {
        if (active) setRacks([]);
      } finally {
        if (active) setRackLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [debouncedRackSearch]);

  const product = products.find(p => p.design_number === designNumber);
  const sizes = useMemo(() => {
    if (!product) return [];
    return [...new Set(product.variations.map(v => v.size))];
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!designNumber || !size || !rackNumber || !quantity) {
      toast.error('Fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/warehouse/inbound`, {
        design_number: designNumber,
        size,
        rack_number: rackNumber,
        quantity: parseInt(quantity),
      });
      toast.success(`Added ${quantity} units to ${rackNumber}`);
      setQuantity('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add stock');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <PackagePlus className="w-5 h-5 text-emerald-600" />
          Add Inbound Stock
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Product (Design Number) *</label>
            <input
              type="text"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search products by design or name..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
              data-testid="inbound-product-search"
            />
            <select
              value={designNumber}
              onChange={(e) => { setDesignNumber(e.target.value); setSize(''); }}
              data-testid="inbound-product-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{productLoading ? 'Searching…' : 'Select product...'}</option>
              {products.map(p => (
                <option key={p.design_number} value={p.design_number}>
                  {p.design_number} — {p.product_name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Size *</label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                disabled={!designNumber}
                data-testid="inbound-size-select"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50"
              >
                <option value="">Select size...</option>
                {sizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quantity *</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="inbound-quantity-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rack *</label>
            <input
              type="text"
              value={rackSearch}
              onChange={(e) => setRackSearch(e.target.value)}
              placeholder="Search racks (e.g. R-201, R-345)..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
              data-testid="inbound-rack-search"
            />
            <select
              value={rackNumber}
              onChange={(e) => setRackNumber(e.target.value)}
              data-testid="inbound-rack-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{rackLoading ? 'Searching…' : 'Select rack...'}</option>
              {racks.map(r => (
                <option key={r.rack_number} value={r.rack_number}>
                  {r.rack_number}{r.name ? ` — ${r.name}` : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inbound-submit-btn"
            className="w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Adding...' : 'Add Stock'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ ADJUSTMENTS TAB ============
const AdjustmentsTab = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [racks, setRacks] = useState([]);
  const [rackLoading, setRackLoading] = useState(false);
  const [designNumber, setDesignNumber] = useState('');
  const [size, setSize] = useState('');
  const [rackNumber, setRackNumber] = useState('');
  const [delta, setDelta] = useState(''); // signed integer
  const [reason, setReason] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);
  const [rackSearch, setRackSearch] = useState('');
  const debouncedRackSearch = useDebounce(rackSearch, 300);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setProductLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '30' });
        if (debouncedProductSearch.trim()) params.set('search', debouncedProductSearch.trim());
        const r = await axios.get(`${API}/products?${params}`);
        if (active) setProducts(Array.isArray(r.data?.products) ? r.data.products : []);
      } catch {
        if (active) setProducts([]);
      } finally {
        if (active) setProductLoading(false);
      }
    })();
    return () => { active = false; };
  }, [debouncedProductSearch]);

  useEffect(() => {
    let active = true;
    (async () => {
      setRackLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '50', active_only: 'true' });
        if (debouncedRackSearch.trim()) params.set('search', debouncedRackSearch.trim());
        const r = await axios.get(`${API}/warehouse/racks?${params}`);
        if (active) setRacks(Array.isArray(r.data?.racks) ? r.data.racks : []);
      } catch {
        if (active) setRacks([]);
      } finally {
        if (active) setRackLoading(false);
      }
    })();
    return () => { active = false; };
  }, [debouncedRackSearch]);

  const product = products.find(p => p.design_number === designNumber);
  const sizes = product ? [...new Set(product.variations.map(v => v.size))] : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(delta);
    if (!designNumber || !size || !rackNumber || !qty || !reason.trim()) {
      toast.error('Fill all fields (quantity can be negative)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/warehouse/adjustments`, {
        design_number: designNumber, size, rack_number: rackNumber, quantity: qty, reason,
      });
      toast.success(`Adjusted ${qty > 0 ? '+' : ''}${qty} on ${rackNumber}`);
      setDelta(''); setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <ArrowDownToLine className="w-5 h-5 text-amber-600" />
          Manual Adjustment
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Positive quantity adds stock. Negative deducts (FIFO within the chosen rack). All actions logged.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Product *</label>
            <input
              type="text"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search product by design or name..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
              data-testid="adjust-product-search"
            />
            <select
              value={designNumber}
              onChange={(e) => { setDesignNumber(e.target.value); setSize(''); }}
              data-testid="adjust-product-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">{productLoading ? 'Searching…' : 'Select product...'}</option>
              {products.map(p => (
                <option key={p.design_number} value={p.design_number}>{p.design_number} — {p.product_name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Size *</label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                disabled={!designNumber}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                data-testid="adjust-size-select"
              >
                <option value="">--</option>
                {sizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rack *</label>
              <input
                type="text"
                value={rackSearch}
                onChange={(e) => setRackSearch(e.target.value)}
                placeholder="Search rack..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
                data-testid="adjust-rack-search"
              />
              <select
                value={rackNumber}
                onChange={(e) => setRackNumber(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="adjust-rack-select"
              >
                <option value="">{rackLoading ? 'Searching…' : '--'}</option>
                {racks.map(r => <option key={r.rack_number} value={r.rack_number}>{r.rack_number}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quantity (± allowed) *</label>
              <input
                type="number"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="e.g. -3 or 5"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="adjust-quantity-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason *</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Damaged stock, sample sent, correction"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="adjust-reason-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="adjust-submit-btn"
            className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Adjustment'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ RACKS TAB ============
const RacksTab = ({ user }) => {
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '50' });
      if (search.trim()) params.set('search', search.trim());
      const r = await axios.get(`${API}/warehouse/racks?${params}`);
      setRacks(r.data.racks || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load racks');
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleActive = async (rack) => {
    if (!isAdmin) return;
    try {
      await axios.patch(`${API}/warehouse/racks/${rack.rack_number}`, { is_active: !rack.is_active });
      toast.success(`${rack.rack_number} ${rack.is_active ? 'disabled' : 'enabled'}`);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search rack number or name..."
            data-testid="warehouse-racks-search"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowAdd(true)}
              data-testid="warehouse-rack-add"
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />Add Rack
            </button>
            <button
              onClick={() => setShowBulk(true)}
              data-testid="warehouse-rack-bulk"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
            >
              Bulk Generate
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 text-sm text-slate-500 border-b border-slate-100">
          {loading ? 'Loading...' : `${total} racks`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Rack #</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-center">Status</th>
                {isAdmin && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {racks.map(r => (
                <tr key={r.rack_number} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono font-semibold">{r.rack_number}</td>
                  <td className="px-4 py-2">{r.name || '-'}</td>
                  <td className="px-4 py-2">{r.location || '-'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => toggleActive(r)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        {r.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>

      {showAdd && <AddRackModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); fetch(); }} />}
      {showBulk && <BulkGenerateModal onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); fetch(); }} />}
    </div>
  );
};

const AddRackModal = ({ onClose, onDone }) => {
  const [rackNumber, setRackNumber] = useState('');
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/warehouse/racks`, { rack_number: rackNumber, name, location });
      toast.success('Rack created');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Add Rack">
      <form onSubmit={submit} className="space-y-3">
        <input required value={rackNumber} onChange={e => setRackNumber(e.target.value)} placeholder="Rack number (e.g. R-351)" className="w-full px-3 py-2 border rounded text-sm" data-testid="add-rack-number" />
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (optional)" className="w-full px-3 py-2 border rounded text-sm" />
        <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Location (optional)" className="w-full px-3 py-2 border rounded text-sm" />
        <button type="submit" disabled={busy} data-testid="add-rack-submit" className="w-full px-4 py-2 bg-indigo-600 text-white rounded font-medium disabled:opacity-50">{busy ? 'Creating...' : 'Create Rack'}</button>
      </form>
    </Modal>
  );
};

const BulkGenerateModal = ({ onClose, onDone }) => {
  const [start, setStart] = useState(351);
  const [end, setEnd] = useState(400);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/warehouse/racks/bulk-generate?start=${start}&end=${end}`);
      toast.success(r.data.message);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Bulk Generate Racks">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Start #</label>
            <input type="number" required value={start} onChange={e => setStart(Number(e.target.value))} className="w-full px-3 py-2 border rounded text-sm" data-testid="bulk-start" />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">End #</label>
            <input type="number" required value={end} onChange={e => setEnd(Number(e.target.value))} className="w-full px-3 py-2 border rounded text-sm" data-testid="bulk-end" />
          </div>
        </div>
        <p className="text-xs text-slate-500">Will create R-XXX from start to end. Existing racks are skipped.</p>
        <button type="submit" disabled={busy} data-testid="bulk-submit" className="w-full px-4 py-2 bg-indigo-600 text-white rounded font-medium disabled:opacity-50">{busy ? 'Generating...' : 'Generate'}</button>
      </form>
    </Modal>
  );
};

// ============ MOVEMENTS TAB ============
const MovementsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('');
  const [design, setDesign] = useState('');
  const [orderNum, setOrderNum] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '50' });
      if (type) params.set('movement_type', type);
      if (design.trim()) params.set('design_number', design.trim());
      if (orderNum.trim()) params.set('order_number', orderNum.trim());
      const r = await axios.get(`${API}/warehouse/movements?${params}`);
      setItems(r.data.movements || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load movements');
    } finally { setLoading(false); }
  }, [page, type, design, orderNum]);

  useEffect(() => { fetch(); }, [fetch]);

  const typeColor = {
    inbound: 'text-emerald-700 bg-emerald-50',
    order_created: 'text-indigo-700 bg-indigo-50',
    order_updated: 'text-indigo-700 bg-indigo-50',
    order_cancelled: 'text-blue-700 bg-blue-50',
    order_deleted: 'text-slate-600 bg-slate-100',
    manual_adjustment: 'text-amber-700 bg-amber-50',
    // legacy values (backward-compat)
    outbound_order: 'text-indigo-700 bg-indigo-50',
    refund_order: 'text-blue-700 bg-blue-50',
    manual_deduct: 'text-rose-700 bg-rose-50',
    manual_adjust: 'text-amber-700 bg-amber-50',
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <select value={type} onChange={e => { setPage(1); setType(e.target.value); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="movements-type-filter">
          <option value="">All types</option>
          <option value="inbound">Inbound</option>
          <option value="order_created">Order Created</option>
          <option value="order_updated">Order Updated</option>
          <option value="order_cancelled">Order Cancelled</option>
          <option value="order_deleted">Order Deleted (audit)</option>
          <option value="manual_adjustment">Manual Adjustment</option>
        </select>
        <input value={design} onChange={e => { setPage(1); setDesign(e.target.value); }} placeholder="Design number" className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="movements-design-filter" />
        <input value={orderNum} onChange={e => { setPage(1); setOrderNum(e.target.value); }} placeholder="Order number" className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="movements-order-filter" />
        <button onClick={fetch} className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center justify-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 text-sm text-slate-500 border-b border-slate-100">{loading ? 'Loading...' : `${total} movements`}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Design</th>
                <th className="px-4 py-2 text-left">Size</th>
                <th className="px-4 py-2 text-left">Rack</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-left">Order</th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && !loading && <tr><td colSpan={9} className="text-center text-slate-400 py-8">No movements yet</td></tr>}
              {items.map(m => (
                <tr key={m.movement_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{formatIST(m.timestamp)}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor[m.type] || 'bg-slate-100 text-slate-700'}`}>{m.type.replace('_', ' ')}</span></td>
                  <td className="px-4 py-2 font-mono text-xs">{m.design_number}</td>
                  <td className="px-4 py-2">{m.size}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.rack_number || '-'}</td>
                  <td className="px-4 py-2 text-right font-semibold">{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.order_number || '-'}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{m.user_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{m.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
};

// ============ OUT-OF-STOCK TAB ============
const OutOfStockTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [productName, setProductName] = useState('');
  const [productType, setProductType] = useState('');

  const debouncedSearch = useDebounce(search, 300);
  const debouncedName = useDebounce(productName, 300);
  const debouncedType = useDebounce(productType, 300);

  const fetchOOS = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '50' });
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (debouncedName.trim()) params.set('product_name', debouncedName.trim());
      if (debouncedType.trim()) params.set('product_type', debouncedType.trim());
      const r = await axios.get(`${API}/warehouse/out-of-stock?${params}`);
      setItems(r.data.items || []);
      setTotalPages(r.data.total_pages || 1);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Failed to load out-of-stock list');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, debouncedName, debouncedType]);

  useEffect(() => { fetchOOS(); }, [fetchOOS]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedName, debouncedType]);

  const hasFilters = !!(debouncedSearch || debouncedName || debouncedType);
  const clear = () => { setSearch(''); setProductName(''); setProductType(''); };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div
          data-testid="oos-summary"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-50 text-rose-800 rounded-full text-sm font-medium"
        >
          <PackageX className="w-4 h-4" />
          {loading ? 'Loading…' : `${total} Out of Stock Product${total === 1 ? '' : 's'}`}
          {hasFilters && (
            <span className="ml-1 text-xs text-rose-600 bg-white border border-rose-200 px-2 py-0.5 rounded">filtered</span>
          )}
        </div>
        <button
          onClick={fetchOOS}
          data-testid="oos-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-xl p-4">
        <FilterInput label="Design Number" value={search} onChange={setSearch} placeholder="e.g. J7122" testid="oos-filter-design" />
        <FilterInput label="Product Name" value={productName} onChange={setProductName} placeholder="Product name" testid="oos-filter-name" />
        <FilterInput label="Product Type (fabric)" value={productType} onChange={setProductType} placeholder="e.g. cotton" testid="oos-filter-type" />
        {hasFilters && (
          <div className="sm:col-span-3 flex justify-end">
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="oos-filter-clear">Clear all filters</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left w-16">Image</th>
                <th className="px-4 py-2 text-left">Design #</th>
                <th className="px-4 py-2 text-left">Product Name</th>
                <th className="px-4 py-2 text-left">Product Type</th>
                <th className="px-4 py-2 text-right">Bulk Price ₹</th>
                <th className="px-4 py-2 text-left">Out of Stock Since</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-400 py-10">
                    {hasFilters ? 'No matching out-of-stock products' : 'No products are out of stock 🎉'}
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr key={it.design_number} className="hover:bg-slate-50" data-testid={`oos-row-${it.design_number}`}>
                  <td className="px-4 py-2">
                    {it.thumbnail || it.primary_image ? (
                      <img src={it.thumbnail || it.primary_image} alt={it.design_number} className="w-12 h-12 object-cover rounded border border-slate-200" loading="lazy" />
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-xs text-slate-400">—</div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono font-semibold text-slate-700">{it.design_number}</td>
                  <td className="px-4 py-2 text-slate-700">{it.product_name || '-'}</td>
                  <td className="px-4 py-2 text-slate-600">{it.product_type || '-'}</td>
                  <td className="px-4 py-2 text-right text-slate-700">
                    {(it.bulk_price ?? null) === null ? '-' : `₹${Number(it.bulk_price).toFixed(2)}`}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{formatIST(it.became_oos_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
};


// ============ HELPERS ============
const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between p-3 border-t border-slate-100 text-sm">
      <span className="text-slate-500">Page {page} of {totalPages}</span>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => onChange(page - 1)} className="px-3 py-1 border border-slate-200 rounded disabled:opacity-40">Prev</button>
        <button disabled={page >= totalPages} onClick={() => onChange(page + 1)} className="px-3 py-1 border border-slate-200 rounded disabled:opacity-40">Next</button>
      </div>
    </div>
  );
};

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">×</button>
      </div>
      {children}
    </div>
  </div>
);

export default Warehouse;
